import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, isRef as _isRef, toDisplayString as _toDisplayString, createBlock as _createBlock, Transition as _Transition, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "p-10 text-center"
}
const _hoisted_2 = {
  key: 1,
  class: "p-6 text-center text-red-600 font-semibold"
}
const _hoisted_3 = {
  key: 2,
  class: "p-4 sm:p-6"
}
const _hoisted_4 = { class: "max-w-5xl mx-auto space-y-6 sm:space-y-8" }
const _hoisted_5 = {
  key: 0,
  class: "text-red-600 text-sm font-medium -mt-4"
}
const _hoisted_6 = { class: "text-xs sm:text-sm text-slate-600 text-center sm:text-left" }
const _hoisted_7 = { class: "font-medium" }
const _hoisted_8 = { class: "font-medium" }
const _hoisted_9 = { class: "flex gap-2 sm:gap-3 w-full sm:w-auto" }

import { ref, watch, computed } from "vue";
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/yup";
import * as yup from "yup";
import { useProductoFindOne } from "../../composables/productoComposables";
import { useMarcaProductoSelect } from "../../composables/marcaProductosComposables";
import { useCategoriaProductoSelect } from "../../composables/categoriaComposables";
import { useProveedorSelect } from "../../composables/proveedorCoposables";
import AdminModalShell from "@/share/components/admin/AdminModalShell.vue";
import AdminModalHeader from "@/share/components/admin/AdminModalHeader.vue";
import AdminModalFooter from "@/share/components/admin/AdminModalFooter.vue";
import AdminButton from "@/share/components/admin/AdminButton.vue";
import InformacionBasica from "./formularioComponents/InformacionBasica.vue";
import EspecificacionesTecnicas from "./formularioComponents/EspecificacionesTecnicas.vue";
import ModelosCompatibles from "./formularioComponents/ModelosCompatibles.vue";
import ImagenesProducto from "./formularioComponents/ImagenesProducto.vue";

interface Props {
  isOpen: boolean;
  productoId?: string;
  isSaving?: boolean;
}
type ProductoFormValues = {
  nombreProducto: string;
  descripcionProducto: string;
  idMarca: string;
  idCategoria: string;
  precioProducto: number | undefined;
  stockProducto: number | undefined;
  isDestacado: number;
};

type BlurField = keyof typeof touched.value;


export default /*@__PURE__*/_defineComponent({
  __name: 'FormularioProducto',
  props: {
    isOpen: { type: Boolean },
    productoId: {},
    isSaving: { type: Boolean, default: false }
  },
  emits: ["close", "save", "update"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const especificaciones = ref<
  { nombreEspTecnicas: string; detallesEspTecncias: string }[]
>([]);
const modelosCompatibles = ref<string[]>([]);
const imagenes = ref<File[]>([]);
const imagenesPreview = ref<string[]>([]);
const archivosExistentesIds = ref<string[]>([]);
const errorImagenes = ref("");

function revokeBlobPreviewUrls(urls: string[]) {
  for (const u of urls) {
    if (u?.startsWith("blob:")) URL.revokeObjectURL(u);
  }
}

const { MarcasProductos } = useMarcaProductoSelect();
const { Categorias } = useCategoriaProductoSelect();
const { Proveedor } = useProveedorSelect();

const isEditMode = computed(() => !!props.productoId);

/** Tipos alineados con vee-validate (`AnyPresentValue` no incluye `null`). */
const productoSchema = toTypedSchema(
  yup.object({
    nombreProducto: yup
      .string()
      .transform((v) => (typeof v === "string" ? v.trim() : v))
      .required("El nombre es obligatorio")
      .min(3, "Debe tener al menos 3 caracteres"),
    descripcionProducto: yup
      .string()
      .optional()
      .max(500, "La descripción no puede superar 500 caracteres"),
    idMarca: yup.string().required("Debe seleccionar una marca"),
    idCategoria: yup.string().required("Debe seleccionar una categoría"),
    precioProducto: yup
      .mixed()
      .test(
        "precio",
        "Ingrese un precio válido",
        (v) =>
          v !== null &&
          v !== undefined &&
          v !== "" &&
          !Number.isNaN(Number(v)) &&
          Number(v) > 0
      ),
    stockProducto: yup
      .mixed()
      .test(
        "stock",
        "Ingrese un stock válido",
        (v) =>
          v !== null &&
          v !== undefined &&
          v !== "" &&
          !Number.isNaN(Number(v)) &&
          Number(v) >= 0
      ),
    isDestacado: yup.number().oneOf([0, 1]).optional(),
  })
);

const {
  errors,
  meta,
  handleSubmit,
  resetForm: resetVeeForm,
  setValues,
  setFieldTouched,
  validateField,
  defineField,
} = useForm<ProductoFormValues>({
  validationSchema: productoSchema,
  initialValues: {
    nombreProducto: "",
    descripcionProducto: "",
    idMarca: "",
    idCategoria: "",
    precioProducto: undefined,
    stockProducto: undefined,
    isDestacado: 0,
  },
});

const [nombreProducto] = defineField("nombreProducto");
const [descripcionProducto] = defineField("descripcionProducto");
const [idMarca] = defineField("idMarca");
const [idCategoria] = defineField("idCategoria");
const [precioProducto] = defineField("precioProducto");
const [stockProducto] = defineField("stockProducto");
const [isDestacado] = defineField("isDestacado");

const touched = ref({
  nombre: false,
  descripcion: false,
  marca: false,
  categoria: false,
  precio: false,
  stock: false,
  imagenes: false,
});

const validaciones = computed(() => ({
  nombre: errors.value.nombreProducto ? [String(errors.value.nombreProducto)] : [],
  descripcion: errors.value.descripcionProducto
    ? [String(errors.value.descripcionProducto)]
    : [],
  marca: errors.value.idMarca ? [String(errors.value.idMarca)] : [],
  categoria: errors.value.idCategoria ? [String(errors.value.idCategoria)] : [],
  precio: errors.value.precioProducto ? [String(errors.value.precioProducto)] : [],
  stock: errors.value.stockProducto ? [String(errors.value.stockProducto)] : [],
  proveedor: [] as string[],
  imagenes: errorImagenes.value
    ? [errorImagenes.value]
    : ([] as string[]),
}));

const imagenesOk = computed(() =>
  isEditMode.value
    ? imagenesPreview.value.length > 0
    : imagenes.value.length > 0
);

const isFormValid = computed(
  () => meta.value.valid && imagenesOk.value && !errorImagenes.value
);

// Funciones para especificaciones
const agregarEspecificacion = () => {
  especificaciones.value.push({
    nombreEspTecnicas: "",
    detallesEspTecncias: "",
  });
};

const eliminarEspecificacion = (index: number) => {
  especificaciones.value.splice(index, 1);
};

// Funciones para modelos compatibles
const agregarModelo = (modelo: string) => {
  modelosCompatibles.value.push(modelo);
};

const eliminarModelo = (index: number) => {
  modelosCompatibles.value.splice(index, 1);
};

// 🔹 MODIFICADO: Función para eliminar imágenes
const eliminarImagen = (index: number) => {
  const previewUrl = imagenesPreview.value[index];
  if (previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(previewUrl);
  }
  // Verificar si la imagen eliminada es una existente (del backend)
  if (index < archivosExistentesIds.value.length) {
    // Es una imagen existente, eliminar su ID
    archivosExistentesIds.value.splice(index, 1);
  } else {
    // Es una imagen nueva (File), calcular el índice correcto
    const indexNuevaImagen = index - archivosExistentesIds.value.length;
    imagenes.value.splice(indexNuevaImagen, 1);
  }

  // Siempre eliminar del preview
  imagenesPreview.value.splice(index, 1);
};

// Cargar datos en modo edición
const productoIdRef = computed(() => props.productoId || "");
const {
  data: productoData,
  isPending,
  isError,
} = useProductoFindOne(productoIdRef);

// 🔹 MODIFICADO: Cargar datos del producto incluyendo IDs de archivos
watch(
  productoData,
  (newData) => {
    const producto = newData?.data?.data;
    if (producto) {
      setValues({
        nombreProducto: producto.nombreProducto || "",
        descripcionProducto: producto.descripcionProducto || "",
        idMarca: producto.idMarca || "",
        idCategoria: producto.idCategoria || "",
        precioProducto:
          producto.precioProducto != null && String(producto.precioProducto) !== ""
            ? Number(producto.precioProducto)
            : undefined,
        stockProducto:
          producto.stockProducto != null
            ? Number(producto.stockProducto)
            : undefined,
        isDestacado: producto.isDestacado ?? 0,
      });

      // Mapear especificaciones técnicas
      especificaciones.value =
        producto.GpEspTecnicasProducto?.map((esp: any) => ({
          nombreEspTecnicas: esp.especificacion?.nombreEspTecnicas || "",
          detallesEspTecncias: esp.especificacion?.detallesEspTecncias || "",
        })) || [];

      // Mapear modelos compatibles
      modelosCompatibles.value =
        producto.GpModeloVehiculoProducto?.map(
          (mv: any) => mv.modeloVehiculo?.id || ""
        ).filter(Boolean) || [];

      // 🔹 MODIFICADO: Guardar IDs y URLs de archivos existentes
      if (
        producto.GpArchivosProducto &&
        producto.GpArchivosProducto.length > 0
      ) {
        revokeBlobPreviewUrls(imagenesPreview.value);
        // Guardar los IDs de los archivos existentes (siempre id de genArchivo, no el id del join)
        archivosExistentesIds.value = producto.GpArchivosProducto.map(
          (arch: { idArchivo?: string; archivo?: { id?: string } }) =>
            arch.idArchivo ?? arch.archivo?.id ?? ""
        ).filter(Boolean);

        // Guardar las URLs para mostrar en el preview
        imagenesPreview.value = producto.GpArchivosProducto.map(
          (arch: any) => arch.archivo?.url || arch.urlArchivo || ""
        ).filter(Boolean);
      } else {
        archivosExistentesIds.value = [];
        revokeBlobPreviewUrls(imagenesPreview.value);
        imagenesPreview.value = [];
      }

      // Limpiar nuevas imágenes al cargar producto existente
      imagenes.value = [];
    }
  },
  { immediate: true }
);

// Limpiar al cerrar
watch(
  () => props.isOpen,
  (isOpen) => {
    if (!isOpen && !isEditMode.value) resetForm();
  }
);

watch([imagenes, imagenesPreview, isEditMode], () => {
  if (imagenesOk.value) errorImagenes.value = "";
});

const veeFieldByBlur: Partial<
  Record<
    BlurField,
    | "nombreProducto"
    | "descripcionProducto"
    | "idMarca"
    | "idCategoria"
    | "precioProducto"
    | "stockProducto"
  >
> = {
  nombre: "nombreProducto",
  descripcion: "descripcionProducto",
  marca: "idMarca",
  categoria: "idCategoria",
  precio: "precioProducto",
  stock: "stockProducto",
};

const handleBlur = (field: BlurField) => {
  touched.value[field] = true;
  const path = veeFieldByBlur[field];
  if (path) {
    setFieldTouched(path, true);
    validateField(path);
  }
};

const submitProducto = handleSubmit((values) => {
  const imgOk = isEditMode.value
    ? imagenesPreview.value.length > 0
    : imagenes.value.length > 0;
  if (!imgOk) {
    errorImagenes.value =
      "Debe enviar al menos una imagen del producto (PNG, JPG, GIF o WEBP, máx. 5MB)";
    touched.value.imagenes = true;
    return;
  }
  errorImagenes.value = "";

  const formData = new FormData();
  formData.append("nombreProducto", String(values.nombreProducto).trim());
  formData.append(
    "descripcionProducto",
    String(values.descripcionProducto ?? "").trim()
  );
  formData.append("idMarca", String(values.idMarca));
  formData.append("idCategoria", String(values.idCategoria));
  formData.append("precioProducto", String(values.precioProducto ?? 0));
  formData.append("stockProducto", String(values.stockProducto ?? 0));
  formData.append("isDestacado", String(values.isDestacado ?? 0));

  formData.append(
    "especificaciones",
    JSON.stringify(
      especificaciones.value.filter(
        (e) => e.nombreEspTecnicas.trim() && e.detallesEspTecncias.trim()
      )
    )
  );

  formData.append(
    "modelosCompatibles",
    JSON.stringify(modelosCompatibles.value)
  );

  if (isEditMode.value) {
    formData.append(
      "archivosExistentes",
      JSON.stringify(archivosExistentesIds.value)
    );
  }

  imagenes.value.forEach((img) => {
    formData.append("imagenes", img);
  });

  if (isEditMode.value && props.productoId) {
    emit("update", props.productoId, formData);
  } else {
    emit("save", formData);
  }
});

const handleSave = async () => {
  const paths = [
    "nombreProducto",
    "descripcionProducto",
    "idMarca",
    "idCategoria",
    "precioProducto",
    "stockProducto",
  ] as const;
  await Promise.all(paths.map((p) => setFieldTouched(p, true)));
  Object.keys(touched.value).forEach((k) => {
    touched.value[k as keyof typeof touched.value] = true;
  });
  submitProducto();
};

// Cerrar modal
const handleClose = () => {
  resetForm();
  emit("close");
};

const resetForm = () => {
  resetVeeForm();
  errorImagenes.value = "";
  especificaciones.value = [];
  modelosCompatibles.value = [];
  imagenes.value = [];
  revokeBlobPreviewUrls(imagenesPreview.value);
  imagenesPreview.value = [];
  archivosExistentesIds.value = [];

  touched.value = {
    nombre: false,
    descripcion: false,
    marca: false,
    categoria: false,
    precio: false,
    stock: false,
    imagenes: false,
  };
};

// Opciones para los selects
const OpcionMarcaProducto = computed(() => {
  return MarcasProductos.value.map((marcas) => ({
    label: marcas.nombreMarca,
    value: marcas.id,
  }));
});

const OpcionCategoria = computed(() => {
  return Categorias.value.map((categoria) => ({
    label: categoria.nombreCategoria,
    value: categoria.id,
  }));
});

const OpcionProveedor = computed(() => {
  return Proveedor.value.map((proveedor) => ({
    label: proveedor.nombreProveedor,
    value: proveedor.id,
  }));
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "fade" }, {
    default: _withCtx(() => [
      (__props.isOpen)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40 flex items-center justify-center p-2 sm:p-4",
            onClick: _withModifiers(handleClose, ["self"])
          }, [
            _createVNode(_Transition, { name: "scale" }, {
              default: _withCtx(() => [
                (__props.isOpen)
                  ? (_openBlock(), _createBlock(AdminModalShell, {
                      key: 0,
                      "max-width-class": "max-w-6xl"
                    }, {
                      header: _withCtx(() => [
                        _createVNode(AdminModalHeader, {
                          variant: "dark",
                          title: isEditMode.value ? 'Editar Producto' : 'Nuevo Producto',
                          subtitle: "Completa la información del producto",
                          onClose: handleClose
                        }, {
                          icon: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                            _createElementVNode("svg", {
                              class: "w-7 h-7",
                              fill: "none",
                              stroke: "currentColor",
                              viewBox: "0 0 24 24"
                            }, [
                              _createElementVNode("path", {
                                "stroke-linecap": "round",
                                "stroke-linejoin": "round",
                                "stroke-width": "2",
                                d: "M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                              })
                            ], -1)
                          ]))]),
                          _: 1
                        }, 8, ["title"])
                      ]),
                      footer: _withCtx(() => [
                        _createVNode(AdminModalFooter, {
                          align: "between",
                          "rounded-bottom": ""
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_6, [
                              _createElementVNode("span", _hoisted_7, _toDisplayString(Object.keys(touched.value).filter(
                    (k) => touched.value[k as keyof typeof touched]
                  ).length), 1),
                              _cache[14] || (_cache[14] = _createTextVNode(" de ", -1)),
                              _createElementVNode("span", _hoisted_8, _toDisplayString(Object.keys(touched.value).length), 1),
                              _cache[15] || (_cache[15] = _createTextVNode(" campos básicos completados ", -1))
                            ]),
                            _createElementVNode("div", _hoisted_9, [
                              _createVNode(AdminButton, {
                                variant: "secondary",
                                onClick: handleClose
                              }, {
                                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                  _createTextVNode(" Cancelar ", -1)
                                ]))]),
                                _: 1
                              }),
                              _createVNode(AdminButton, {
                                variant: "primary",
                                disabled: 
                    !isFormValid.value || (_unref(isPending) && isEditMode.value) || props.isSaving
                  ,
                                loading: props.isSaving,
                                onClick: handleSave
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(props.isSaving
                      ? "Guardando…"
                      : isEditMode.value
                        ? "Actualizar"
                        : "Guardar"), 1)
                                ]),
                                _: 1
                              }, 8, ["disabled", "loading"])
                            ])
                          ]),
                          _: 1
                        })
                      ]),
                      default: _withCtx(() => [
                        (_unref(isPending) && isEditMode.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [...(_cache[12] || (_cache[12] = [
                              _createElementVNode("div", { class: "animate-spin h-12 w-12 border-b-2 border-red-600 rounded-full mx-auto" }, null, -1),
                              _createElementVNode("p", { class: "text-slate-600 mt-4" }, "Cargando datos...", -1)
                            ]))]))
                          : (_unref(isError) && isEditMode.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[13] || (_cache[13] = [
                                _createElementVNode("svg", {
                                  class: "w-16 h-16 mx-auto mb-4",
                                  fill: "none",
                                  stroke: "currentColor",
                                  viewBox: "0 0 24 24"
                                }, [
                                  _createElementVNode("path", {
                                    "stroke-linecap": "round",
                                    "stroke-linejoin": "round",
                                    "stroke-width": "2",
                                    d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                  })
                                ], -1),
                                _createTextVNode(" Error al cargar el producto ", -1)
                              ]))]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                _createElementVNode("div", _hoisted_4, [
                                  _createVNode(InformacionBasica, {
                                    nombreProducto: _unref(nombreProducto),
                                    "onUpdate:nombreProducto": _cache[0] || (_cache[0] = ($event: any) => (_isRef(nombreProducto) ? (nombreProducto).value = $event : null)),
                                    descripcionProducto: _unref(descripcionProducto),
                                    "onUpdate:descripcionProducto": _cache[1] || (_cache[1] = ($event: any) => (_isRef(descripcionProducto) ? (descripcionProducto).value = $event : null)),
                                    idMarca: _unref(idMarca),
                                    "onUpdate:idMarca": _cache[2] || (_cache[2] = ($event: any) => (_isRef(idMarca) ? (idMarca).value = $event : null)),
                                    idCategoria: _unref(idCategoria),
                                    "onUpdate:idCategoria": _cache[3] || (_cache[3] = ($event: any) => (_isRef(idCategoria) ? (idCategoria).value = $event : null)),
                                    precioProducto: _unref(precioProducto),
                                    "onUpdate:precioProducto": _cache[4] || (_cache[4] = ($event: any) => (_isRef(precioProducto) ? (precioProducto).value = $event : null)),
                                    stockProducto: _unref(stockProducto),
                                    "onUpdate:stockProducto": _cache[5] || (_cache[5] = ($event: any) => (_isRef(stockProducto) ? (stockProducto).value = $event : null)),
                                    isDestacado: _unref(isDestacado),
                                    "onUpdate:isDestacado": _cache[6] || (_cache[6] = ($event: any) => (_isRef(isDestacado) ? (isDestacado).value = $event : null)),
                                    touched: touched.value,
                                    validaciones: validaciones.value,
                                    marcas: OpcionMarcaProducto.value,
                                    categorias: OpcionCategoria.value,
                                    proveedores: OpcionProveedor.value,
                                    onBlur: handleBlur
                                  }, null, 8, ["nombreProducto", "descripcionProducto", "idMarca", "idCategoria", "precioProducto", "stockProducto", "isDestacado", "touched", "validaciones", "marcas", "categorias", "proveedores"]),
                                  _createVNode(EspecificacionesTecnicas, {
                                    especificaciones: especificaciones.value,
                                    "onUpdate:especificaciones": _cache[7] || (_cache[7] = ($event: any) => ((especificaciones).value = $event)),
                                    onAgregar: agregarEspecificacion,
                                    onEliminar: eliminarEspecificacion
                                  }, null, 8, ["especificaciones"]),
                                  _createVNode(ModelosCompatibles, {
                                    modelosCompatibles: modelosCompatibles.value,
                                    "onUpdate:modelosCompatibles": _cache[8] || (_cache[8] = ($event: any) => ((modelosCompatibles).value = $event)),
                                    onAgregar: agregarModelo,
                                    onEliminar: eliminarModelo
                                  }, null, 8, ["modelosCompatibles"]),
                                  _createVNode(ImagenesProducto, {
                                    imagenes: imagenes.value,
                                    "onUpdate:imagenes": _cache[9] || (_cache[9] = ($event: any) => ((imagenes).value = $event)),
                                    imagenesPreview: imagenesPreview.value,
                                    "onUpdate:imagenesPreview": _cache[10] || (_cache[10] = ($event: any) => ((imagenesPreview).value = $event)),
                                    "requiere-imagen": !isEditMode.value,
                                    onEliminar: eliminarImagen
                                  }, null, 8, ["imagenes", "imagenesPreview", "requiere-imagen"]),
                                  (validaciones.value.imagenes.length)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(validaciones.value.imagenes[0]), 1))
                                    : _createCommentVNode("", true)
                                ])
                              ]))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})